
import { Component, Vue, Prop, Watch, Emit } from 'vue-property-decorator'
import { formatYearMonthDay } from '@xt/client/utils/my'
import EmptyStatus from '@xt/www/components/emptyStatus/index.vue'
import CourseRecordCard from '../CourseRecordCard/index.vue'
import CourseScheduleCard from '../CourseScheduleCard/index.vue'
import { EnumEmptyStatus } from '@xt/client/utils/empty-status/index'
import type { CalendarTimeData, StudyRecordResp } from '@xt/client/entities/my-v2/types'
import type { ICalendarData } from '@xt/client/types/my-go-course'

type DateType = Omit<CalendarTimeData, 'w' | 'timestamp'>

@Component({
  components: {
    EmptyStatus,
    CourseRecordCard,
    CourseScheduleCard
  }
})
export default class CalendarMonthPop extends Vue {
  visible: boolean = false

  currentDate: number = 0
  currentTime: DateType = {
    y: 0,
    m: 0,
    d: 0
  }
  activeDate: DateType = {
    y: 0,
    m: 0,
    d: 0
  }

  // 日历数据，学习记录，课时列表
  calendarTimeData: Array<CalendarTimeData> = []
  activeStudyRecord: Array<StudyRecordResp> = []
  activeClassHour: Array<ICalendarData> = []

  limitRange: { min: DateType; max: DateType } = {
    min: formatYearMonthDay(this.$ajax.serviceDate().subtract(18, 'month').valueOf()),
    max: formatYearMonthDay(this.$ajax.serviceDate().add(18, 'month').valueOf())
  }

  get EnumEmptyStatus() {
    return EnumEmptyStatus
  }

  get PageStore() {
    return this.$store.$myV2.Classroom
  }

  getDayOfWeekUrl(index: number) {
    return `${this.$store.$global.static}/images/my-new/pc/my-classroom-calendar-pop-text-im${index}.png`
  }

  isExistClasshour(item: CalendarTimeData | null) {
    if (!item) return false

    return (
      (this.PageStore.cacheClasshourList[`${item.y}-${item.m}`] ?? []).findIndex(v => {
        // 如果是头部临时课 ｜ 尾部临时课
        if (v.classhourType !== 1) {
          return v.day === item.d && v.month === item.m && v.year === item.y && v.publishStatus === 1
        }
        return v.day === item.d && v.month === item.m && v.year === item.y
      }) > -1
    )
  }

  isActive(item: CalendarTimeData | null) {
    if (!item) return false

    return item.y === this.activeDate.y && item.m === this.activeDate.m && item.d === this.activeDate.d
  }

  isToday(item: CalendarTimeData | null) {
    const now = formatYearMonthDay(this.currentDate)

    if (!item) return false

    return item.y === now.y && item.m === now.m && item.d === now.d
  }

  handleActiveChange(item: DateType) {
    this.activeDate = {
      ...item
    }
    this.updateActiveClassHour(item, `${item.y}-${item.m}`)
    this.updateActiveStudyRecord(item, `${item.y}-${item.m}`)
  }

  updateActiveClassHour(date: DateType, key: string) {
    const list = this.PageStore.cacheClasshourList[key].filter(
      item =>
        item.day === date.d && item.month === date.m && item.year === date.y && (item.classhourType !== 1 ? item.publishStatus === 1 : true)
    )
    this.activeClassHour = [...list]
  }

  updateActiveStudyRecord(date: DateType, key: string) {
    const list = this.PageStore.cacheStudyRecord[key].filter(item => item.day === date.d && item.month === date.m && item.year === date.y)
    this.activeStudyRecord = [...list]
  }

  setCalendarTimeData(data: Array<CalendarTimeData>) {
    this.calendarTimeData = [...data]
  }

  handlePreMonth() {
    const { m, y } = this.currentTime

    const year = m === 1 ? y - 1 : y
    const month = m === 1 ? 12 : m - 1

    if (year < this.limitRange.min.y || (year === this.limitRange.min.y && month < this.limitRange.min.m)) return

    this.currentTime = {
      y: year,
      m: month,
      d: 1
    }

    this.setCalendarTimeData(this.PageStore.cacheCalendarTime[`${year}-${month}`])
  }

  handleNextMonth() {
    const { m, y } = this.currentTime
    const year = m === 12 ? y + 1 : y
    const month = m === 12 ? 1 : m + 1

    if (year > this.limitRange.max.y || (year === this.limitRange.max.y && month > this.limitRange.max.m)) return

    this.currentTime = {
      y: year,
      m: month,
      d: 1
    }

    this.setCalendarTimeData(this.PageStore.cacheCalendarTime[`${year}-${month}`])
  }

  async init() {
    this.currentDate = this.$ajax.serviceDate().valueOf()
    this.currentTime = this.activeDate = formatYearMonthDay(this.currentDate)

    const key = `${this.currentTime.y}-${this.currentTime.m}`
    // 初始化当前时间的日历数据，课时数据，学习记录数据
    const calendarTimeData = this.PageStore.get31DayCalendarData()

    this.PageStore.loadCacheCalendarTimeData(calendarTimeData, key)
    this.setCalendarTimeData(this.PageStore.cacheCalendarTime[key])

    await this.PageStore.loadCacheClassHourList(calendarTimeData[0].timestamp, key)
    this.updateActiveClassHour(this.currentTime, key)

    await this.PageStore.loadCacheStudyRecord(calendarTimeData[0].timestamp, key)
    this.updateActiveStudyRecord(this.currentTime, key)
  }

  @Watch('visible')
  onVisibleChange(val: boolean) {
    if (!val) {
      this.PageStore.resetCalendarPopCache()
      return
    }

    this.init()
  }

  @Watch('currentTime', { deep: true })
  onCurrentTimeChange(val: DateType) {
    const prevTimeData = this.PageStore.getPrevMonthTimeData(val)
    const nextTimeData = this.PageStore.getNextMonthTimeData(val)
    this.PageStore.loadCacheClassHourList(prevTimeData.data[0].timestamp, prevTimeData.cacheKey)
    this.PageStore.loadCacheClassHourList(nextTimeData.data[0].timestamp, nextTimeData.cacheKey)

    this.PageStore.loadCacheStudyRecord(prevTimeData.data[0].timestamp, prevTimeData.cacheKey)
    this.PageStore.loadCacheStudyRecord(nextTimeData.data[0].timestamp, nextTimeData.cacheKey)

    this.PageStore.loadCacheCalendarTimeData(prevTimeData.data, prevTimeData.cacheKey)
    this.PageStore.loadCacheCalendarTimeData(nextTimeData.data, nextTimeData.cacheKey)
  }

  openPop() {
    this.visible = true
  }

  hidePop() {
    this.visible = false
  }

  @Emit('closed')
  onClosed() {}

  created(): void {}

  mounted(): void {}

  updated(): void {}

  destroyed(): void {
    console.log('destroyed', this.visible)
  }
}
